import type { AIReviewInstallationSettingsResponse } from '@/app/api/ai-review-settings/[installationId]/global/types';
import type { AppendIgnorePatternsResponse } from '@/app/api/ai-review-settings/[installationId]/ignore-patterns/types';
import type { AIReviewSettings } from '@/lib/ai-review-settings/types';

import type { AIReviewSettingsInput } from './mutate-helpers';

import { parseAIReviewSettingsApiError } from './api-error';
import {
  buildInstallationSettingsUpdateRequestBody,
  buildUpdateRequestBody,
  DEFAULT_UPDATE_ERROR,
  throwOnSettingsUpdateError,
} from './mutate-helpers';

import { AI_REVIEW_SETTINGS_ROUTE } from '@/app/api/ai-review-settings/[installationId]/[repositoryId]/constants';
import { AIReviewInstallationSettingsResponseSchema } from '@/app/api/ai-review-settings/[installationId]/global/types';
import { AppendIgnorePatternsResponseSchema } from '@/app/api/ai-review-settings/[installationId]/ignore-patterns/types';
import { AiReviewSettingsResponseSchema } from '@/lib/ai-review-settings/schema';

export const DEFAULT_FETCH_ERROR = 'Failed to fetch AI review settings';
const PERMISSION_DENIED_FETCH_ERROR = "You don't have permission to view settings";

export class SettingsFetchError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'SettingsFetchError';
  }
}

export function shouldRetrySettingsFetch(failureCount: number, error: unknown): boolean {
  if (
    error instanceof SettingsFetchError &&
    error.status >= 400 &&
    error.status < 500 &&
    error.status !== 429
  ) {
    return false;
  }

  return failureCount < 3;
}

function throwOnSettingsFetchError(response: Response, payload: unknown, logPrefix: string): void {
  if (response.ok) {
    return;
  }

  const { message: serverMessage } = parseAIReviewSettingsApiError(payload);

  if (serverMessage) {
    console.error(`${logPrefix} Fetch failed:`, serverMessage);
  }

  throw new SettingsFetchError(
    response.status === 403 ? PERMISSION_DENIED_FETCH_ERROR : DEFAULT_FETCH_ERROR,
    response.status,
  );
}

export async function patchAIReviewSettings(
  installationId: number,
  repositoryId: number,
  newSettings: AIReviewSettingsInput,
): Promise<AIReviewSettings> {
  const body = buildUpdateRequestBody(installationId, repositoryId, newSettings);
  const response = await fetch(`${AI_REVIEW_SETTINGS_ROUTE}/${installationId}/${repositoryId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify(body),
  });

  let payload: unknown = null;

  try {
    payload = await response.json();
  } catch (error) {
    console.error('[useAIReviewSettings] Failed to parse update response', error);
    throw new Error(DEFAULT_UPDATE_ERROR, { cause: error });
  }

  throwOnSettingsUpdateError(response, payload, '[useAIReviewSettings]');

  return AiReviewSettingsResponseSchema.parse(payload);
}

export async function getAIReviewInstallationSettings(
  installationId: number,
  signal?: AbortSignal,
): Promise<AIReviewInstallationSettingsResponse> {
  const response = await fetch(`${AI_REVIEW_SETTINGS_ROUTE}/${installationId}/global`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
    signal,
  });

  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch (error) {
    console.error('[useAIReviewInstallationSettings] Failed to parse response', error);
    throw new SettingsFetchError(DEFAULT_FETCH_ERROR, response.status);
  }

  throwOnSettingsFetchError(response, payload, '[useAIReviewInstallationSettings]');
  return AIReviewInstallationSettingsResponseSchema.parse(payload);
}

export async function patchAIReviewInstallationSettings(
  installationId: number,
  newSettings: AIReviewSettingsInput,
): Promise<AIReviewInstallationSettingsResponse> {
  const response = await fetch(`${AI_REVIEW_SETTINGS_ROUTE}/${installationId}/global`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify(buildInstallationSettingsUpdateRequestBody(newSettings)),
  });

  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch (error) {
    console.error('[useAIReviewInstallationSettings] Failed to parse update response', error);
    throw new Error(DEFAULT_UPDATE_ERROR, { cause: error });
  }

  throwOnSettingsUpdateError(response, payload, '[useAIReviewInstallationSettings]');
  return AIReviewInstallationSettingsResponseSchema.parse(payload);
}

export async function postAppendIgnorePatterns(
  installationId: number,
  patterns: string[],
): Promise<AppendIgnorePatternsResponse> {
  const response = await fetch(`${AI_REVIEW_SETTINGS_ROUTE}/${installationId}/ignore-patterns`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify({ patterns }),
  });

  let payload: unknown = null;

  try {
    payload = await response.json();
  } catch (error) {
    console.error('[useAppendIgnorePatterns] Failed to parse append response', error);
    throw new Error(DEFAULT_UPDATE_ERROR, { cause: error });
  }

  throwOnSettingsUpdateError(response, payload, '[useAppendIgnorePatterns]');

  return AppendIgnorePatternsResponseSchema.parse(payload);
}
